/**
 * Persistence of runs and results: the current daily and endless runs (resumed on the next
 * visit), one result per day for the first attempt, and the endless personal best. Formats are
 * in spec/formats.md.
 */
import {
  dayNumber,
  expeditionNumber,
  LAUNCH_DAY,
  type Mode,
  parseRunRecord,
  type RunRecord,
} from "@mistsweeper/core";
import { getJson, remove, setJson } from "./storage.ts";

export interface DailyResult {
  readonly v: 1;
  readonly day: number;
  readonly expedition: number;
  readonly outcome: "dead" | "sealed";
  readonly metres: number;
  readonly tiles: number;
  readonly shareMap: readonly string[];
  readonly record: RunRecord;
}

export interface EndlessBest {
  readonly v: 1;
  readonly metres: number;
  readonly tiles: number;
  readonly seed: number;
  readonly at: number;
}

/**
 * The current run of a mode: a bare RunRecord, as spec/formats.md keeps it (Daniel, 2026-10-21:
 * the web follows the spec). Until then the web wrapped it as `{v, record, attempt}`; that is
 * still read, the record taken and the flag left, since `isAttempt` works it out.
 */
export function loadRun(mode: Mode): RunRecord | null {
  const saved = getJson<{ record?: unknown }>(`run.${mode}`);
  if (!saved) return null;
  try {
    const record = parseRunRecord("record" in saved ? saved.record : saved);
    return record.mode === mode ? record : null;
  } catch {
    return null;
  }
}

export function saveRun(record: RunRecord): void {
  setJson(`run.${record.mode}`, record);
}

/**
 * Whether a daily run is its day's attempt, the one that counts: while its day has no result,
 * and once its death is the day's result. A sealed attempt that goes on is practice, as is every
 * run started after the result. The app works it out the same way (`isAttempt` in
 * MistsweeperGame's GameController).
 */
export function isAttempt(record: RunRecord): boolean {
  if (record.mode !== "daily" || record.day === null) return false;
  const result = loadDailyResult(record.day);
  if (!result) return true;
  return result.outcome === "dead" && sameRecord(result.record, record);
}

/** Whether two run records are the same run, action by action. */
function sameRecord(a: RunRecord, b: RunRecord): boolean {
  return (
    a.world === b.world &&
    a.mode === b.mode &&
    a.seed === b.seed &&
    a.day === b.day &&
    a.startedAt === b.startedAt &&
    a.actions.length === b.actions.length &&
    a.actions.every((x, i) => {
      const y = b.actions[i];
      return y !== undefined && x[0] === y[0] && x[1] === y[1] && x[2] === y[2];
    })
  );
}

export function clearRun(mode: Mode): void {
  remove(`run.${mode}`);
}

export function loadDailyResult(day: number): DailyResult | null {
  const r = getJson<DailyResult>(`daily.${day}`);
  return r && r.v === 1 && r.day === day ? r : null;
}

export function saveDailyResult(result: DailyResult): void {
  setJson(`daily.${result.day}`, result);
}

export function loadEndlessBest(): EndlessBest | null {
  const b = getJson<EndlessBest>("endless.best");
  return b && b.v === 1 ? b : null;
}

export function saveEndlessBest(best: EndlessBest): void {
  setJson("endless.best", best);
}

export interface EndlessStats {
  readonly v: 1;
  readonly runs: number;
  readonly last: number;
}

export function loadEndlessStats(): EndlessStats {
  const s = getJson<EndlessStats>("endless.stats");
  return s && s.v === 1 ? s : { v: 1, runs: 0, last: 0 };
}

/** Counts an endless run that has ended. */
export function recordEndlessRun(metres: number): EndlessStats {
  const stats: EndlessStats = { v: 1, runs: loadEndlessStats().runs + 1, last: metres };
  setJson("endless.stats", stats);
  return stats;
}

/** The days from today back to the first expedition, at most `limit` of them, with their results. */
export function dailyHistory(
  now: number,
  limit: number,
): { readonly day: number; readonly result: DailyResult | null }[] {
  const today = dayNumber(now);
  const out = [];
  for (let day = today; day >= LAUNCH_DAY && out.length < limit; day -= 1) {
    out.push({ day, result: loadDailyResult(day) });
  }
  return out;
}

/** The furthest any daily attempt has reached, and on which day; null before the first. */
export function bestDaily(now: number): { readonly day: number; readonly metres: number } | null {
  let best: { day: number; metres: number } | null = null;
  for (const { day, result } of dailyHistory(now, 400)) {
    if (result && (!best || result.metres > best.metres)) best = { day, metres: result.metres };
  }
  return best;
}

/** Consecutive days with a result, counting back from today or yesterday. */
export function streak(now: number): number {
  const today = dayNumber(now);
  let day = loadDailyResult(today) ? today : today - 1;
  let n = 0;
  while (loadDailyResult(day)) {
    n += 1;
    day -= 1;
  }
  return n;
}

export function expeditionOf(day: number): number {
  return expeditionNumber(day);
}
